import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { toast } from 'react-toastify';
import XtramModal, {
    XtramBadge,
    XtramButton,
    XtramCheckbox,
    XtramField,
    XtramInput,
    XtramNotice,
    XtramSearch,
    XtramSelect,
    XtramTable,
    xtramStyles,
} from '../../Widgets/XtramModal/XtramModal.jsx';
import EnumsService from '../../../Services/enums.service.js';
import styles from './OtherDocumentsXtram.module.scss';

const ALL_TITLES = '__all__';
const DEFAULT_SECTION = '__default__';

const normalize = (value) => (value || '').trim().toLowerCase();

const readError = async (response) => {
    try {
        return await response.text();
    } catch {
        return '';
    }
};

/**
 * Every document type (OtherDocumentsType) of the accountant: list, add, edit (name, category,
 * client visibility) and delete with reassignment. `onChanged()` reloads the page's types.
 */
const OtherDocumentTypesModal = ({
    open,
    onClose,
    onChanged,
    t,
    direction,
    accountantId,
    token,
    types = [],
    titles = [],
    startWithAdd = false,
    defaultTitleId = null,
}) => {
    const [busy, setBusy] = useState(false);
    const [usage, setUsage] = useState({});
    const [search, setSearch] = useState('');
    const [titleFilter, setTitleFilter] = useState(ALL_TITLES);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ name: '', titleId: '', hidden: false });
    const [submitted, setSubmitted] = useState(false);
    const [deleteType, setDeleteType] = useState(null);
    const [reassignTo, setReassignTo] = useState(null);

    const loadUsage = useCallback(async () => {
        if (!accountantId || !token) return;
        try {
            const response = await EnumsService.GetOtherDocumentsTypeUsage(accountantId, token);
            if (response.ok) setUsage((await response.json()) || {});
        } catch (error) {
            console.error('Error loading document type usage:', error);
        }
    }, [accountantId, token]);

    const titleById = useMemo(() => new Map(titles.map((title) => [title.id, title])), [titles]);
    const titleOptions = useMemo(
        () => [{ value: '', label: t('DefaultSection') }, ...titles.map((title) => ({ value: title.id, label: title.name }))],
        [titles, t]
    );
    const filterOptions = useMemo(
        () => [
            { value: ALL_TITLES, label: t('All') },
            { value: DEFAULT_SECTION, label: t('DefaultSection') },
            ...titles.map((title) => ({ value: title.id, label: title.name })),
        ],
        [titles, t]
    );
    const sortedTypes = useMemo(
        () => [...types].filter((type) => type?.id).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
        [types]
    );

    const openForm = useCallback((type) => {
        setSubmitted(false);
        setEditing(type || 'new');
        setForm(type
            ? { name: type.name || '', titleId: titleById.has(type.titleId) ? type.titleId : '', hidden: !!type.hiddenFromClient }
            : { name: '', titleId: defaultTitleId || '', hidden: false });
    }, [titleById, defaultTitleId]);

    useEffect(() => {
        if (!open) return;
        setSearch('');
        setTitleFilter(ALL_TITLES);
        setDeleteType(null);
        setEditing(null);
        if (startWithAdd) openForm(null);
        loadUsage();
        // Reset only when the modal opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const countOf = (name) => usage[normalize(name)] || 0;
    const titleIdOf = (type) => (titleById.has(type.titleId) ? type.titleId : '');

    const visibleTypes = sortedTypes.filter((type) => {
        const titleId = titleIdOf(type);
        if (titleFilter === DEFAULT_SECTION && titleId) return false;
        if (titleFilter !== ALL_TITLES && titleFilter !== DEFAULT_SECTION && titleId !== titleFilter) return false;
        return !search.trim() || normalize(type.name).includes(normalize(search));
    });

    const send = async (request, errorKey) => {
        const response = await request();
        if (response.ok) return true;
        const errorText = await readError(response);
        toast.error(response.status === 409 ? t('OtherDocumentsCategoryExists') : `${t(errorKey)}: ${errorText}`);
        return false;
    };

    const withBusy = async (action) => {
        setBusy(true);
        try {
            return await action();
        } catch (error) {
            console.error(error);
            toast.error(t('Error on save OtherDocumentsType data'));
            return false;
        } finally {
            setBusy(false);
        }
    };

    const refresh = async () => {
        await onChanged?.();
        await loadUsage();
    };

    // ─── Add / edit ─────────────────────────────
    const isNew = editing === 'new';
    const editedType = isNew ? null : editing;
    const formName = form.name.trim();
    const nameTaken = !!formName && types.some((type) => type.id !== editedType?.id && normalize(type.name) === normalize(formName));

    const saveForm = async () => {
        setSubmitted(true);
        if (!formName || nameTaken || busy) return;
        const ok = await withBusy(async () => {
            if (isNew) {
                return send(
                    () => EnumsService.AddOtherDocumentsType(accountantId, formName, token, form.hidden, form.titleId || null),
                    'CategoryAddError'
                );
            }
            const renamed = formName !== editedType.name;
            const hiddenChanged = form.hidden !== !!editedType.hiddenFromClient;
            const titleChanged = form.titleId !== titleIdOf(editedType);
            if ((renamed || hiddenChanged) && !await send(
                () => EnumsService.UpdateOtherDocumentsType(accountantId, formName, editedType.name, token, form.hidden),
                'Error on save OtherDocumentsType data'
            )) return false;
            if (titleChanged && !await send(
                () => EnumsService.AssignTypesToOtherDocumentsTitle(accountantId, form.titleId || null, [editedType.id], token),
                'TypesAssignError'
            )) return false;
            return true;
        });
        if (!ok) return;
        toast.success(t(isNew ? 'CategoryAddedSuccessfully' : 'OtherDocumentsTypeUpdated'));
        setEditing(null);
        await refresh();
    };

    const setTypeHidden = async (type, hidden) => {
        const ok = await withBusy(() => send(
            () => EnumsService.UpdateOtherDocumentsType(accountantId, type.name, type.name, token, hidden),
            'Error on save OtherDocumentsType data'
        ));
        if (!ok) return;
        toast.success(t(hidden ? 'CategoryHiddenFromClient' : 'CategoryShownToClient'));
        await refresh();
    };

    // ─── Delete ─────────────────────────────────
    const openDelete = (type) => {
        setReassignTo(null);
        setDeleteType({ ...type, count: countOf(type.name) });
    };

    const confirmDelete = async () => {
        if (!deleteType || busy || (deleteType.count > 0 && !reassignTo)) return;
        await withBusy(async () => {
            const response = await EnumsService.DeleteOtherDocumentsType(deleteType.id, token, reassignTo?.value || null);
            if (response.ok) {
                toast.success(t('OtherDocumentsCategoryDeleted'));
                setDeleteType(null);
                await refresh();
            } else if (response.status === 409) {
                const body = await response.json().catch(() => ({}));
                setDeleteType((prev) => ({ ...prev, count: body?.documentCount || prev.count || 1 }));
            } else {
                toast.error(`${t('OtherDocumentsCategoryDeleteError')}: ${await readError(response)}`);
            }
        });
    };

    const reassignOptions = deleteType
        ? sortedTypes.filter((type) => type.id !== deleteType.id).map((type) => ({ value: type.name, label: type.name }))
        : [];

    return (
        <>
            <XtramModal
                open={open}
                onClose={onClose}
                title={t('DocumentTypes')}
                direction={direction}
                wide
                busy={busy}
                footer={(
                    <>
                        <span className={xtramStyles.footerInfo}>
                            {t('OtherDocumentsTypesCount', { shown: visibleTypes.length, total: sortedTypes.length })}
                        </span>
                        <XtramButton variant="secondary" onClick={onClose} disabled={busy}>{t('close')}</XtramButton>
                    </>
                )}
            >
                <div className={xtramStyles.toolbar}>
                    <div className={xtramStyles.toolbarGroup}>
                        <XtramSearch direction={direction} value={search} onChange={setSearch} placeholder={t('Search')} />
                        {titles.length > 0 && (
                            <div className={styles.itemControl}>
                                <XtramSelect
                                    direction={direction}
                                    options={filterOptions}
                                    value={filterOptions.find((option) => option.value === titleFilter) || filterOptions[0]}
                                    onChange={(option) => setTitleFilter(option?.value || ALL_TITLES)}
                                    disableClearable
                                />
                            </div>
                        )}
                    </div>
                    <XtramButton onClick={() => openForm(null)} disabled={busy}>
                        <AddIcon fontSize="small" />
                        {t('AddCategory')}
                    </XtramButton>
                </div>

                {visibleTypes.length === 0 ? (
                    <div className={xtramStyles.emptyState}>{t('NoDocumentTypes')}</div>
                ) : (
                    <XtramTable height="tall">
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('DocumentTypeName')}</TableCell>
                                    <TableCell>{t('OtherDocumentsTitle')}</TableCell>
                                    <TableCell className={xtramStyles.actionsCell}>{t('HiddenFromClient')}</TableCell>
                                    <TableCell className={xtramStyles.actionsCell}>{t('OtherDocumentsDocumentsInCategory')}</TableCell>
                                    <TableCell className={xtramStyles.actionsCell}>{t('Actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleTypes.map((type) => {
                                    const title = titleById.get(type.titleId);
                                    return (
                                        <TableRow key={type.id} hover className={type.hiddenFromClient || title?.hiddenFromClient ? xtramStyles.rowDimmed : ''}>
                                            <TableCell>
                                                <span className={styles.nameCell}>
                                                    {type.name}
                                                    {title?.hiddenFromClient && <XtramBadge tone="warning">{t('OtherDocumentsHiddenByTitle')}</XtramBadge>}
                                                </span>
                                            </TableCell>
                                            <TableCell>{title ? title.name : t('DefaultSection')}</TableCell>
                                            <TableCell className={xtramStyles.actionsCell}>
                                                <Checkbox
                                                    size="small"
                                                    className={xtramStyles.tableCheckbox}
                                                    checked={!!type.hiddenFromClient}
                                                    onChange={(e) => setTypeHidden(type, e.target.checked)}
                                                    disabled={busy}
                                                    inputProps={{ 'aria-label': t('HiddenFromClient') }}
                                                />
                                            </TableCell>
                                            <TableCell className={xtramStyles.actionsCell}>
                                                <span className={styles.countPill}>{countOf(type.name)}</span>
                                            </TableCell>
                                            <TableCell className={xtramStyles.actionsCell}>
                                                <Tooltip title={t('Edit')}>
                                                    <span>
                                                        <IconButton size="small" onClick={() => openForm(type)} disabled={busy}>
                                                            <EditOutlinedIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title={t('Delete')}>
                                                    <span>
                                                        <IconButton size="small" onClick={() => openDelete(type)} disabled={busy}>
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </XtramTable>
                )}
            </XtramModal>

            <XtramModal
                open={open && !!editing}
                onClose={() => setEditing(null)}
                title={isNew ? t('AddCategory') : t('OtherDocumentsEditType')}
                direction={direction}
                busy={busy}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target.tagName === 'INPUT' && e.target.type !== 'checkbox') saveForm();
                }}
                footer={(
                    <>
                        <XtramButton variant="secondary" onClick={() => setEditing(null)} disabled={busy}>{t('Cancel')}</XtramButton>
                        <XtramButton onClick={saveForm} disabled={busy || nameTaken} busy={busy}>{t(isNew ? 'Add' : 'Save')}</XtramButton>
                    </>
                )}
            >
                <div className={styles.stack}>
                    <div className={styles.fieldGrid}>
                        <XtramField
                            label={t('DocumentTypeName')}
                            required
                            className={styles.spanAll}
                            error={nameTaken ? t('OtherDocumentsCategoryExists') : submitted && !formName ? t('RequiredField') : null}
                            hint={isNew ? t('AddCategoryDescription') : null}
                        >
                            <XtramInput
                                direction={direction}
                                autoFocus
                                value={form.name}
                                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                                placeholder={t('DocumentTypeName')}
                                error={nameTaken || (submitted && !formName)}
                            />
                        </XtramField>
                        <XtramField label={t('OtherDocumentsTitle')} className={styles.spanAll}>
                            <XtramSelect
                                direction={direction}
                                options={titleOptions}
                                value={titleOptions.find((option) => option.value === form.titleId) || titleOptions[0]}
                                onChange={(option) => setForm((prev) => ({ ...prev, titleId: option?.value || '' }))}
                                disableClearable
                            />
                        </XtramField>
                        <div className={styles.spanAll}>
                            <XtramCheckbox
                                checked={form.hidden}
                                onChange={(hidden) => setForm((prev) => ({ ...prev, hidden }))}
                                label={t('HideCategoryFromClient')}
                            />
                        </div>
                    </div>
                    {editedType && (
                        <>
                            {editedType.name !== formName && countOf(editedType.name) > 0 && (
                                <XtramNotice type="info">
                                    {t('OtherDocumentsTypeRenameNotice', { count: countOf(editedType.name) })}
                                </XtramNotice>
                            )}
                            <dl className={styles.infoGrid}>
                                <div className={styles.infoItem}>
                                    <dt>{t('OtherDocumentsDocumentsInCategory')}</dt>
                                    <dd>{countOf(editedType.name)}</dd>
                                </div>
                                <div className={styles.infoItem}>
                                    <dt>{t('OtherDocumentsTitle')}</dt>
                                    <dd>{titleById.get(editedType.titleId)?.name || t('DefaultSection')}</dd>
                                </div>
                                <div className={`${styles.infoItem} ${styles.spanAll}`}>
                                    <dt>{t('OtherDocumentsTypeId')}</dt>
                                    <dd className={styles.idText} title={editedType.id}>{editedType.id}</dd>
                                </div>
                            </dl>
                        </>
                    )}
                </div>
            </XtramModal>

            <XtramModal
                open={open && !!deleteType}
                onClose={() => setDeleteType(null)}
                title={t('OtherDocumentsDeleteCategory')}
                direction={direction}
                busy={busy}
                footer={(
                    <>
                        <XtramButton variant="secondary" onClick={() => setDeleteType(null)} disabled={busy}>{t('Cancel')}</XtramButton>
                        <XtramButton
                            variant="danger"
                            onClick={confirmDelete}
                            disabled={busy || (deleteType?.count > 0 && !reassignTo)}
                            busy={busy}
                        >
                            {deleteType?.count > 0 ? t('OtherDocumentsMoveAndDelete') : t('Delete')}
                        </XtramButton>
                    </>
                )}
            >
                {deleteType && (
                    <div className={styles.stack}>
                        <span>{t('OtherDocumentsDeleteCategoryConfirm', { name: deleteType.name })}</span>
                        {deleteType.count > 0 && (
                            <>
                                <XtramNotice type="warning">
                                    {t('OtherDocumentsCategoryInUse', { count: deleteType.count })}
                                </XtramNotice>
                                <XtramField label={t('OtherDocumentsMoveDocumentsTo')} required size="full">
                                    <XtramSelect
                                        direction={direction}
                                        options={reassignOptions}
                                        value={reassignTo}
                                        onChange={setReassignTo}
                                        placeholder={t('Document Type')}
                                    />
                                </XtramField>
                            </>
                        )}
                    </div>
                )}
            </XtramModal>
        </>
    );
};

export default OtherDocumentTypesModal;
